import { useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessagesSquareIcon, StarIcon, XIcon } from 'lucide-react';
import {
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorNotice,
  PageHeading,
  Panel,
  SearchInput,
  SkeletonTable,
  cn,
} from '@bcecloud/portal-ui';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { errorMessage, getJson, sendJson } from '@/lib/api';
import { useUrlSearch } from '@/lib/use-list-query-state';
import { whenLabel } from '@/pages/assistant/shared';

/**
 * Chat history — each row links to the conversation's own page
 * (/assistant/chats/:id). Chats are strictly personal: one staff member can
 * never read another's; shared knowledge belongs in org skills and memory.
 */

interface ChatListItem {
  readonly id: string;
  readonly title: string;
  readonly pinned: boolean;
  readonly updatedAt: string | null;
}

export function ChatsPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const [deleting, setDeleting] = useState<ChatListItem | null>(null);
  const { search, setSearch, debounced } = useUrlSearch();

  // The server holds the search: the list is capped at the newest 50, and a
  // search should still find an older chat by its title.
  const chats = useQuery({
    queryKey: ['assistant-chats', debounced],
    queryFn: ({ signal }) =>
      getJson<ChatListItem[]>('/assistant/chats', debounced ? { search: debounced } : undefined, signal),
    placeholderData: keepPreviousData,
  });

  const togglePin = useMutation({
    mutationFn: (chat: ChatListItem) =>
      sendJson('put', `/assistant/chats/${chat.id}`, { pinned: !chat.pinned }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['assistant-chats'] }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => sendJson('delete', `/assistant/chats/${id}`),
    onSuccess: async () => {
      setDeleting(null);
      await queryClient.invalidateQueries({ queryKey: ['assistant-chats'] });
    },
  });

  return (
    <TooltipProvider delayDuration={100}>
      <section className="animate-portal-rise">
        <PageHeading
          eyebrow="AI ASSISTANT"
          title="Chats"
          subtitle="Your conversations with the assistant — only you can see these."
        />
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <SearchInput
            label="Search chats"
            placeholder="Chat title"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="w-full max-w-sm"
          />
        </div>
        {chats.isPending ? (
          <SkeletonTable label="Loading chats…" />
        ) : chats.isError ? (
          <ErrorNotice title="Chats unavailable" message={errorMessage(chats.error)} />
        ) : chats.data.length === 0 && debounced !== '' ? (
          <Panel>
            <EmptyState title="No chats match" message="Try a different search." />
          </Panel>
        ) : chats.data.length === 0 ? (
          <Panel>
            <EmptyState
              icon={<MessagesSquareIcon aria-hidden className="size-6" />}
              title="No chats yet"
              message="Conversations with the assistant save here automatically. Pin the ones you come back to."
            />
          </Panel>
        ) : (
          <DataTable headers={['Chat', 'Last activity', '']} minWidth={560}>
            {chats.data.map((chat) => (
              <tr key={chat.id} className="border-t border-portal-line align-top">
                <td className="p-3">
                  <Link
                    to={`/assistant/chats/${chat.id}`}
                    className="flex items-center gap-1.5 text-portal-small font-bold text-brand hover:underline"
                  >
                    {chat.pinned ? (
                      <StarIcon
                        aria-hidden
                        className="size-3 shrink-0 fill-amber-400 text-amber-400"
                      />
                    ) : null}
                    <span className="truncate">{chat.title}</span>
                  </Link>
                </td>
                <td className="p-3 text-portal-small text-portal-muted">
                  {whenLabel(chat.updatedAt) ?? '—'}
                </td>
                <td className="p-3">
                  <span className="flex items-center justify-end gap-0.5">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          aria-label={chat.pinned ? 'Unpin chat' : 'Pin chat to the top'}
                          aria-pressed={chat.pinned}
                          onClick={() => togglePin.mutate(chat)}
                          className={cn(
                            'grid size-6 cursor-pointer place-items-center rounded-md hover:bg-admin-subtle',
                            chat.pinned ? 'text-amber-500' : 'text-portal-muted hover:text-amber-500',
                          )}
                        >
                          <StarIcon
                            aria-hidden
                            className={cn('size-3.5', chat.pinned && 'fill-amber-400')}
                          />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">
                        {chat.pinned ? 'Unpin' : 'Pin to top'}
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          aria-label="Remove chat"
                          onClick={() => setDeleting(chat)}
                          className="grid size-6 cursor-pointer place-items-center rounded-md text-portal-muted hover:bg-red-50 hover:text-red-600"
                        >
                          <XIcon aria-hidden className="size-3.5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">Remove</TooltipContent>
                    </Tooltip>
                  </span>
                </td>
              </tr>
            ))}
          </DataTable>
        )}

        <ConfirmDialog
          open={deleting !== null}
          tone="danger"
          title="Delete this chat?"
          message={deleting ? `"${deleting.title}" will be permanently removed.` : ''}
          confirmLabel="Delete chat"
          onConfirm={() => {
            if (deleting) remove.mutate(deleting.id);
          }}
          onCancel={() => {
            if (!remove.isPending) setDeleting(null);
          }}
          pending={remove.isPending}
        />
      </section>
    </TooltipProvider>
  );
}
